import React from 'react';
import '@/App.css';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SyncProvider } from './context/SyncContext';
import { homeRouteForRole } from './lib/homeRoute';
// import { Analytics } from '@vercel/analytics/react';

// Components
import AuthModal from './components/AuthModal';
import Layout from './components/Layout';
import EcranChargement from './components/EcranChargement';

// Pages
import PilotagePage from './pages/PilotagePage';
import Dashboard from './pages/Dashboard';
import SearchPage from './pages/SearchPage';
import TripsPage from './pages/TripsPage';
import TripDetailPage from './pages/TripDetailPage';
import NewTripPage from './pages/NewTripPage';
import TrucksPage from './pages/TrucksPage';
import TruckDetailPage from './pages/TruckDetailPage';
import DriversPage from './pages/DriversPage';
import OperationsPage from './pages/OperationsPage';
import OperationDetailPage from './pages/OperationDetailPage';
import CompaniesPage from './pages/CompaniesPage';
import CompteSocietePage from './pages/CompteSocietePage';
import ClientsPage from './pages/ClientsPage';
import OrdersPage from './pages/OrdersPage';
import InvoicesPage from './pages/InvoicesPage';
import StockPage from './pages/StockPage';
import FinancePage from './pages/FinancePage';
import PricingRulesPage from './pages/PricingRulesPage';
import AlertsPage from './pages/AlertsPage';
import MissionIntegrityPage from './pages/MissionIntegrityPage';
import FieldOpsPage from './pages/FieldOpsPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ControlFuelPage from './pages/ControlFuelPage';
import MaintenancePage from './pages/MaintenancePage';
import { RedirectTruckMaintenance } from './pages/TruckMaintenancePage';
import RentalsPage from './pages/RentalsPage';
import RentalContractPage from './pages/RentalContractPage';
import InboundRentalContractPage from './pages/InboundRentalContractPage';
import TransportStatementPage from './pages/TransportStatementPage';
import ReportsPage from './pages/ReportsPage';
import AccesClientsPage from './pages/AccesClientsPage';
import ExportsPage from './pages/ExportsPage';
import DailyReportsPage from './pages/DailyReportsPage';
import StationsHomePage from './pages/stations/StationsHomePage';
import ShiftPage from './pages/stations/ShiftPage';
import StationReportsPage from './pages/stations/StationReportsPage';
import StationSettingsPage from './pages/stations/StationSettingsPage';
import StationPricesPage from './pages/stations/StationPricesPage';
import TanksPage from './pages/stations/TanksPage';
import ReceptionPage from './pages/stations/ReceptionPage';
import SupplyPage from './pages/stations/SupplyPage';
import StationCreditPage from './pages/stations/StationCreditPage';
import StationNetworkPage from './pages/stations/StationNetworkPage';
import StationCompliancePage from './pages/stations/StationCompliancePage';
import StationAnnexesPage from './pages/stations/StationAnnexesPage';
import LotsDashboardPage from './pages/lots/LotsDashboardPage';
import LotsListPage from './pages/lots/LotsListPage';
import LotDetailPage from './pages/lots/LotDetailPage';
import LotsSuiviPage from './pages/lots/LotsSuiviPage';
import LotsRetoursPage from './pages/lots/LotsRetoursPage';
import LotsPaiementsPage from './pages/lots/LotsPaiementsPage';
import LotPaiementsPage from './pages/lots/LotPaiementsPage';
import LotDecomptePage from './pages/lots/LotDecomptePage';
import DispatchPage from './pages/DispatchPage';
import { AuditSecuritePage, JournalActionsPage } from './pages/AuditLogsPage';
import PlateformeSantePage from './pages/PlateformeSantePage';
import DemonstrationPage from './pages/DemonstrationPage';
import ActiviteSocietesPage from './pages/ActiviteSocietesPage';
import DocumentsPage from './pages/DocumentsPage';
import SharedTripView from './pages/SharedTripView';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AccountDeletionPage from './pages/AccountDeletionPage';
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
import FleetImportPage from './pages/FleetImportPage';
import SmartImportPage from './pages/SmartImportPage';
import UsersPage from './pages/UsersPage';
import TrackingPage from './pages/TrackingPage';
import GeoLocationsPage from './pages/GeoLocationsPage';
import GpsProvidersPage from './pages/GpsProvidersPage';
import OrgBlockingSettingsPage from './pages/OrgBlockingSettingsPage';
import OpenWaPage from './pages/OpenWaPage';
import SuperAdminHome from './pages/SuperAdminHome';
import BackendOpsPage from './pages/BackendOpsPage';
import LicensesPage from './pages/LicensesPage';
import OnboardingPage from './pages/OnboardingPage';
import ProfilePage from './pages/ProfilePage';
import { ONBOARDING_ROUTE } from './components/onboarding';

// Protected Route wrapper
/** Cuves et réceptions : ouvertes aux cuves comme aux achats (mêmes droits que l'API). */
const MODULES_CUVES = ['tanks', 'purchases'];

const ProtectedRoute = ({ children, allowedRoles, module }) => {
  const { isAuthenticated, isLoading, user, hasModule } = useAuth();

  if (isLoading) {
    // Pendant la vérification de session et jusqu'au premier écran: le logo animé.
    return <EcranChargement />;
  }

  if (!isAuthenticated) {
    return <AuthModal />;
  }

  // Role-based access control
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = user?.role;
    if (!allowedRoles.includes(userRole)) {
      return (
        <Layout>
          <div className="p-8 text-center">
            <h2 className="mb-2 text-2xl font-bold text-foreground">Accès refusé</h2>
            <p className="text-muted-foreground">
              Vous n'avez pas les permissions pour accéder à cette page.
            </p>
          </div>
        </Layout>
      );
    }
  }

  // `module` peut être une liste : un seul module suffit (comme `require_any_module` côté serveur).
  if (module && !(Array.isArray(module) ? module.some((code) => hasModule(code)) : hasModule(module))) {
    return (
      <Layout>
        <div className="p-8 text-center">
          <h2 className="mb-2 text-2xl font-bold text-foreground">Module désactivé</h2>
          <p className="text-muted-foreground">
            Ce module n'est pas actif pour votre organisation.
          </p>
        </div>
      </Layout>
    );
  }

  return <Layout>{children}</Layout>;
};

// Home dispatcher: the primary pack decides the first screen. Extra modules enrich that screen later.
const HomeDispatcher = () => {
  const { user, isSuperAdmin, isClientPortal, activePack, hasModule } = useAuth();
  const roleHome = homeRouteForRole(user?.role);
  if (roleHome) return <Navigate to={roleHome} replace />;
  if (isSuperAdmin) return <SuperAdminHome />;
  if (isClientPortal) return <ClientPortalPage />;
  if (activePack === 'CONTROLE_CARBURANT') return <PilotagePage />;
  // Pack Stations (lot 030) : l'accueil est la journée de la station (plusieurs
  // stations : celle retenue, le réseau est dans le menu).
  if (activePack === 'STATIONS_RESEAU') return <StationsHomePage />;
  if (activePack === 'TRANSPORT_DISTRIBUTION' || hasModule('trips')) return <Dashboard />;
  return <PilotagePage />;
};

// App Routes
const AppRoutes = () => {
  const location = useLocation();

  return (
      <Routes location={location}>
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/account-deletion" element={<AccountDeletionPage />} />
        <Route path="/shared/trip/:id" element={<SharedTripView />} />
        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <AuditSecuritePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <JournalActionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sante"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <PlateformeSantePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/demonstration"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <DemonstrationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/societes-activite"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ActiviteSocietesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute module="documents">
              <DocumentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizations"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <OrganizationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizations/:id"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <OrganizationDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/organizations/:id/fleet-import"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <FleetImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import-intelligent"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <SmartImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fleet-import"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_SOCIETE']}>
              <FleetImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_SOCIETE']}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        {/* Les profils d'accès se gèrent depuis « Utilisateurs »: l'ancienne
            adresse (favoris, liens) y renvoie, liste des profils ouverte. */}
        <Route path="/profiles" element={<Navigate to="/users?profils=1" replace />} />
        {/* « Suivi en direct » a fusionné dans « Où sont mes camions » : un seul
            écran de suivi. L'ancienne adresse (favoris, liens) y renvoie. */}
        <Route path="/live-tracking" element={<Navigate to="/tracking" replace />} />
        {/* Ouvert à l'ADMIN_SOCIETE depuis que l'API distingue les zones
            partagées (postes frontières, lecture seule pour tous) des zones
            privées d'un client. Sans cette ouverture, une société ne pouvait pas
            déclarer ses propres sites de livraison — donc pas s'en servir. */}
        <Route
          path="/geo-locations"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_SOCIETE']}>
              <GeoLocationsPage />
            </ProtectedRoute>
          }
        />
        {/* Sans prop `module`: un écran de paramétrage doit rester joignable
            quel que soit le pack acheté par la société. */}
        <Route
          path="/parametres/blocages"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_SOCIETE']}>
              <OrgBlockingSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gps-providers"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_SOCIETE']} module="gps_tracking">
              <GpsProvidersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/openwa"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <OpenWaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/licenses"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <LicensesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/backend-ops"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <BackendOpsPage />
            </ProtectedRoute>
          }
        />
        {/* Sans prop `module`: le parcours choisit lui-même ses étapes d'après
            la licence, et les fermer toutes vaut mieux que renvoyer « module
            désactivé » à quelqu'un qui vient de se connecter pour la première
            fois. Réservé à l'ADMIN_SOCIETE: les quatre créations écrivent dans
            l'organisation de qui les lance, et un SUPER_ADMIN n'en a pas. */}
        <Route
          path={ONBOARDING_ROUTE}
          element={
            <ProtectedRoute allowedRoles={['ADMIN_SOCIETE']}>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomeDispatcher />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-portal"
          element={
            <ProtectedRoute>
              <ClientPortalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <SearchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trips"
          element={
            <ProtectedRoute module="trips">
              <TripsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trips/new"
          element={
            <ProtectedRoute module="trips">
              <NewTripPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trips/:id"
          element={
            <ProtectedRoute module="trips">
              <TripDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trucks"
          element={
            <ProtectedRoute module="trucks">
              <TrucksPage />
            </ProtectedRoute>
          }
        />
        {/* Fiche camion: identité, papiers, trajets et atelier d'un même
            véhicule. Chaque onglet vérifie lui-même son module. */}
        <Route
          path="/trucks/:truckId"
          element={
            <ProtectedRoute module="trucks">
              <TruckDetailRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="/drivers"
          element={
            <ProtectedRoute module="drivers">
              <DriversPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operations"
          element={
            <ProtectedRoute module="operations">
              <OperationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/operations/:id"
          element={
            <ProtectedRoute module="operations">
              <OperationDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <ProtectedRoute module="companies">
              <CompaniesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies/:id/compte"
          element={
            <ProtectedRoute module="companies">
              <CompteSocietePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients"
          element={
            <ProtectedRoute module="clients">
              <ClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedRoute module="orders">
              <OrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/invoices"
          element={
            <ProtectedRoute module="invoices">
              <InvoicesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock"
          element={
            <ProtectedRoute module="stock_movements">
              <StockPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <ProtectedRoute module="finance_cockpit">
              <FinancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pricing-rules"
          element={
            <ProtectedRoute module="pricing_rules">
              <PricingRulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mission-integrity"
          element={
            <ProtectedRoute module="volume_reconciliation">
              <MissionIntegrityPage />
            </ProtectedRoute>
          }
        />
        {/* Gated on `trips`: these screens read what drivers record while
            running a trip, and the backend read routes carry no module guard of
            their own — gating on a code the server does not enforce would only
            be theatre. */}
        <Route
          path="/field-ops"
          element={
            <ProtectedRoute module="trips">
              <FieldOpsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <ProtectedRoute module="alerts">
              <AlertsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance"
          element={
            <ProtectedRoute module="maintenance">
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance/readings"
          element={
            <ProtectedRoute module="maintenance">
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance/plans"
          element={
            <ProtectedRoute module="maintenance">
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance/work-orders"
          element={
            <ProtectedRoute module="maintenance">
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance/breakdowns"
          element={
            <ProtectedRoute module="maintenance">
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance/documents"
          element={
            <ProtectedRoute module="maintenance">
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/maintenance/immobilizations"
          element={
            <ProtectedRoute module="maintenance">
              <MaintenancePage />
            </ProtectedRoute>
          }
        />
        {/* Ancienne fiche maintenance d'un camion: elle est devenue l'onglet
            Maintenance de la fiche camion. Les liens déjà partagés y mènent. */}
        <Route
          path="/maintenance/trucks/:truckId"
          element={
            <ProtectedRoute module="maintenance">
              <RedirectTruckMaintenance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rentals"
          element={
            <ProtectedRoute module="truck_rentals">
              <RentalsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rentals/:id"
          element={
            <ProtectedRoute module="truck_rentals">
              <RentalContractPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rentals/inbound/:id"
          element={
            <ProtectedRoute module="truck_rentals">
              <InboundRentalContractPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rentals/inbound/:id/etats/nouveau"
          element={
            <ProtectedRoute module="truck_rentals">
              <TransportStatementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rentals/inbound/:id/etats/:statementId"
          element={
            <ProtectedRoute module="truck_rentals">
              <TransportStatementPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rapports"
          element={
            <ProtectedRoute module="reports">
              <ReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/acces-clients"
          element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_SOCIETE']} module="external_integrations">
              <AccesClientsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/exports"
          element={
            <ProtectedRoute module="reports">
              <ExportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-fuel"
          element={
            <ProtectedRoute module="fuel_consumption">
              <ControlFuelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-fuel/sites"
          element={
            <ProtectedRoute module="sites">
              <ControlFuelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-fuel/assets"
          element={
            <ProtectedRoute module="assets">
              <ControlFuelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-fuel/requests"
          element={
            <ProtectedRoute module="fuel_requests">
              <ControlFuelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-fuel/issues"
          element={
            <ProtectedRoute module="fuel_issues">
              <ControlFuelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-fuel/quotas"
          element={
            <ProtectedRoute module="fuel_quotas">
              <ControlFuelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/control-fuel/history"
          element={
            <ProtectedRoute module="fuel_consumption">
              <ControlFuelPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/daily-reports"
          element={
            <ProtectedRoute module="reports">
              <DailyReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations"
          element={
            <ProtectedRoute module="stations">
              <StationsHomePage />
            </ProtectedRoute>
          }
        />
        {/* Lot 030 : la caisse et les indicateurs d'avant sont remplacés par le poste et le rapport. */}
        <Route path="/stations/pos" element={<Navigate to="/stations/poste" replace />} />
        <Route path="/stations/kpis" element={<Navigate to="/stations/rapports" replace />} />
        <Route
          path="/stations/poste"
          element={
            <ProtectedRoute module="shifts">
              <ShiftPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/:stationId/poste"
          element={
            <ProtectedRoute module="shifts">
              <ShiftPage />
            </ProtectedRoute>
          }
        />
        {/* L'ancienne page « Achats » : une réception de camion se fait désormais à l'écran des cuves (lot 031). */}
        <Route path="/stations/purchases" element={<Navigate to="/stations/cuves" replace />} />
        <Route
          path="/stations/rapports"
          element={
            <ProtectedRoute module="station_daily_report">
              <StationReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/cuves"
          element={
            <ProtectedRoute module={MODULES_CUVES}>
              <TanksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/receptions/:id"
          element={
            <ProtectedRoute module={MODULES_CUVES}>
              <ReceptionPage />
            </ProtectedRoute>
          }
        />
        {/* Lot 032 : réapprovisionnement de mes stations (menu Transport, « Mes stations »). */}
        <Route
          path="/stations/reapprovisionnement"
          element={
            <ProtectedRoute module="stations">
              <SupplyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/reglages"
          element={
            <ProtectedRoute module="stations">
              <StationSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/prix"
          element={
            <ProtectedRoute module="prices">
              <StationPricesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/clients"
          element={
            <ProtectedRoute module="station_credit">
              <StationCreditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/reseau"
          element={
            <ProtectedRoute module="multi_station_dashboard">
              <StationNetworkPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/conformite"
          element={
            <ProtectedRoute module="reports">
              <StationCompliancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stations/annexes"
          element={
            <ProtectedRoute module="station_annexes">
              <StationAnnexesPage />
            </ProtectedRoute>
          }
        />
        {/* Lots attribués (lot 057) : camions chargés achetés à un transporteur partenaire. */}
        <Route
          path="/lots"
          element={
            <ProtectedRoute module="lots">
              <LotsDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lots/liste"
          element={
            <ProtectedRoute module="lots">
              <LotsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lots/suivi"
          element={
            <ProtectedRoute module="lots">
              <LotsSuiviPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lots/retours"
          element={
            <ProtectedRoute module="lots">
              <LotsRetoursPage />
            </ProtectedRoute>
          }
        />
        {/* Paiement du partenaire (lot 058) : situation, acomptes et décomptes d'un lot. */}
        <Route
          path="/lots/paiements"
          element={
            <ProtectedRoute module="lots">
              <LotsPaiementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lots/:id/paiements"
          element={
            <ProtectedRoute module="lots">
              <LotPaiementsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lots/:id/decomptes/nouveau"
          element={
            <ProtectedRoute module="lots">
              <LotDecomptePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lots/:id/decomptes/:sid"
          element={
            <ProtectedRoute module="lots">
              <LotDecomptePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lots/:id"
          element={
            <ProtectedRoute module="lots">
              <LotDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dispatch"
          element={
            <ProtectedRoute module="dispatch_allocations">
              <DispatchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tracking"
          element={
            <ProtectedRoute module="gps_tracking">
              <TrackingPage />
            </ProtectedRoute>
          }
        />
        {/* /admin deprecated; redirect to home (SuperAdminHome for super admins) */}
        <Route path="/admin" element={<Navigate to="/" replace />} />
        {/* Fallback route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
  );
};


// Remonte la fiche à chaque camion: passer de /trucks/A à /trucks/B (recherche
// globale) gardait sinon le profil de A, et « Lever l'immobilisation » visait
// l'immobilisation de A pendant le chargement de B.
function TruckDetailRoute() {
  const { truckId } = useParams();
  return <TruckDetailPage key={truckId} />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SyncProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" />
            {/* <Analytics /> */}
          </BrowserRouter>
        </SyncProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
